import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { CourseSet } from "../../types/requirement.response";
import { AddableCourse, CourseLite, CourseOption, CourseSummary } from "../../types/course.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { DEFAULT_SEMESTER } from "../../constants";
import { getCoursesFromCourseSet } from "../../utils/requirementResponseUtils";
import { sortCoursesById } from "../../utils/sortUtils";
import { useHydratedCourses } from "../../hooks/useHydratedCourses";
import CourseRow from "../course-row/CourseRow";

interface GroupOptionsModalProps {
    courseSet: CourseSet;
    coursesMap: Record<string, CourseSummary>;
    relevantSubjects: string[];
    requirementId: string;
    handleAddCourse: (newCourse: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void;
    handleDeleteCourse: (deletedCourse: CourseLite) => void;
    handleFavoriteCourse: (courseOption: CourseLite) => void;
    optimisticFavorites: Map<string, boolean>;
    userCourses?: CourseOption[];
    onClose: () => void;
}

export default function GroupOptionsModal({
    courseSet,
    coursesMap,
    relevantSubjects,
    requirementId,
    handleAddCourse,
    handleDeleteCourse,
    handleFavoriteCourse,
    optimisticFavorites,
    userCourses,
    onClose,
}: GroupOptionsModalProps) {
    // 该组全部非考试课程,按课号排序
    const courses = useMemo(
        () => sortCoursesById(
            getCoursesFromCourseSet(courseSet, coursesMap, relevantSubjects).filter(c => c.type !== 'EXAM')
        ),
        [courseSet, coursesMap, relevantSubjects]
    );
    const hydrated = useHydratedCourses(courses);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    return createPortal(
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {courseSet.title || "Course options"}
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Complete 1 course from this group · {courses.length} options
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        aria-label="Close"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="overflow-y-auto px-6 py-2">
                    <table className="w-full border-collapse">
                        <tbody>
                            {courses.map(summary => (
                                <CourseRow
                                    key={summary.mapKey}
                                    course={summary}
                                    hydrated={hydrated.get(summary.mapKey)}
                                    selectedSemester={DEFAULT_SEMESTER}
                                    onAddToSchedule={(course, classSectionIds) => handleAddCourse(course, [], DEFAULT_SEMESTER, classSectionIds)}
                                    onMarkAsTaken={(course) => handleAddCourse(course, [], null)}
                                    onDeleteFromSchedule={(course) => handleDeleteCourse(course)}
                                    onFavoriteToggle={(course) => handleFavoriteCourse(course)}
                                    optimisticFavorites={optimisticFavorites}
                                    userCourses={userCourses}
                                    requirementId={requirementId}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>,
        document.body
    );
}
